import React, { useState } from 'react';
import '../App.css';

interface Personality {
  id: string;
  name: string;
  difficulty: string;
  description: string;
}

interface Evaluation {
  sessionId: string;
  scores: {
    confidence: number;
    fluency: number;
    objectionHandling: number;
    slideCoverage: number;
  };
  coveredPoints: string[];
  missedPoints: string[];
  objectionsEncountered: string[];
  objectionHandlingNotes: string[];
  actionableFeedback: string[];
}

interface TranscriptMessage {
  role: string;
  content: string;
}

function Roleplay() {
  const [personalities, setPersonalities] = useState<Personality[]>([]);
  const [selectedPersonality, setSelectedPersonality] = useState<string>('');
  const [advisorName, setAdvisorName] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  React.useEffect(() => {
    fetch('http://localhost:3001/api/personalities')
      .then(res => res.json())
      .then(data => setPersonalities(data.personalities))
      .catch(err => console.error('Error fetching personalities:', err));
  }, []);

  const startSession = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          personalityId: selectedPersonality,
          advisorName: advisorName,
          objectives: ['Cover compound interest', 'Mention emergency fund']
        })
      });
      const data = await response.json();
      setSessionId(data.sessionId);
      setIsSessionActive(true);
      setTranscript([{ role: 'system', content: 'Session started with ' + selectedPersonality + ' personality' }]);
    } catch (error) {
      console.error('Error starting session:', error);
    }
  };

  const sendMessage = async () => {
    if (!sessionId || !messageInput.trim() || sending) return;
    const content = messageInput.trim();
    setMessageInput('');
    setSending(true);

    // Show the advisor message immediately for instant feedback.
    setTranscript(prev => [...prev, { role: 'advisor', content }]);

    try {
      const response = await fetch(`http://localhost:3001/api/sessions/${sessionId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content })
      });
      const data = await response.json();
      if (data.clientResponse) {
        setTranscript(prev => [...prev, { role: 'client', content: data.clientResponse }]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setTranscript(prev => [...prev, { role: 'system', content: 'Error: Failed to get client response.' }]);
    } finally {
      setSending(false);
    }
  };

  const endSession = async () => {
    if (!sessionId) return;
    try {
      // End the session.
      await fetch(`http://localhost:3001/api/sessions/${sessionId}/end`, {
        method: 'POST'
      });
      setIsSessionActive(false);
      setTranscript(prev => [...prev, { role: 'system', content: 'Session ended. Evaluating...' }]);

      // Automatically evaluate and show the feedback modal.
      const evalResponse = await fetch(`http://localhost:3001/api/sessions/${sessionId}/evaluate`, {
        method: 'POST'
      });
      const evalData: Evaluation = await evalResponse.json();
      setEvaluation(evalData);
      setShowFeedback(true);
    } catch (error) {
      console.error('Error ending session:', error);
      setTranscript(prev => [...prev, { role: 'system', content: 'Error: Failed to evaluate session.' }]);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', fontFamily: 'Arial' }}>
      <h1>Financial Roleplay Training</h1>

      {!isSessionActive ? (
        <div style={{ border: '1px solid #ccc', padding: '20px', borderRadius: '8px' }}>
          <h2>Start a New Session</h2>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>Advisor Name</label>
            <input
              type="text"
              value={advisorName}
              onChange={(e) => setAdvisorName(e.target.value)}
              style={{ width: '100%', padding: '8px', fontSize: '16px' }}
              placeholder="Enter your name"
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>Select Client Personality</label>
            <select
              value={selectedPersonality}
              onChange={(e) => setSelectedPersonality(e.target.value)}
              style={{ width: '100%', padding: '8px', fontSize: '16px' }}
            >
              <option value="">Select a personality...</option>
              {personalities.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.difficulty})
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={startSession}
            disabled={!selectedPersonality || !advisorName}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Start Session
          </button>
        </div>
      ) : (
        <div style={{ border: '1px solid #ccc', padding: '20px', borderRadius: '8px' }}>
          <h2>Session in Progress</h2>
          <p><strong>Session ID:</strong> {sessionId}</p>
          <p><strong>Advisor:</strong> {advisorName}</p>

          <div style={{
            border: '1px solid #ddd',
            padding: '15px',
            borderRadius: '4px',
            height: '300px',
            overflowY: 'auto',
            backgroundColor: '#f9f9f9',
            marginBottom: '15px'
          }}>
            {transcript.map((msg, idx) => (
              <div key={idx} style={{
                marginBottom: '5px',
                color: msg.role === 'system' ? '#666' : '#000'
              }}>
                <strong>{msg.role}:</strong> {msg.content}
              </div>
            ))}
          </div>

          {/* Message input — replaced the old "Send Test Message" button. */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <input
              type="text"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type your message as the advisor..."
              disabled={sending}
              style={{
                flex: 1,
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!messageInput.trim() || sending}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </div>

          <button
            onClick={endSession}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            End Session
          </button>
        </div>
      )}

      {/* Feedback Modal — shown after evaluation completes. */}
      {showFeedback && evaluation && (
        <div className="modal-overlay" onClick={() => setShowFeedback(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Session Feedback</h2>
              <button className="modal-close" onClick={() => setShowFeedback(false)}>✕</button>
            </div>

            <div className="modal-body">
              <h3>Performance Scores</h3>
              <div className="score-cards">
                <div className="score-card">
                  <div className="score-label">Confidence</div>
                  <div className="score-value">{evaluation.scores.confidence}<span className="score-max">/10</span></div>
                </div>
                <div className="score-card">
                  <div className="score-label">Fluency</div>
                  <div className="score-value">{evaluation.scores.fluency}<span className="score-max">/10</span></div>
                </div>
                <div className="score-card">
                  <div className="score-label">Obj. Handling</div>
                  <div className="score-value">{evaluation.scores.objectionHandling}<span className="score-max">/10</span></div>
                </div>
                <div className="score-card">
                  <div className="score-label">Coverage</div>
                  <div className="score-value">{evaluation.scores.slideCoverage}<span className="score-max">%</span></div>
                </div>
              </div>

              <h3>✅ Covered Points</h3>
              <ul className="check-list">
                {evaluation.coveredPoints.length > 0 ? (
                  evaluation.coveredPoints.map((point, i) => (
                    <li key={i} className="covered">{point}</li>
                  ))
                ) : (
                  <li className="empty">None recorded</li>
                )}
              </ul>

              <h3>❌ Missed Points</h3>
              <ul className="check-list">
                {evaluation.missedPoints.length > 0 ? (
                  evaluation.missedPoints.map((point, i) => (
                    <li key={i} className="missed">{point}</li>
                  ))
                ) : (
                  <li className="empty">None — great job!</li>
                )}
              </ul>

              {evaluation.objectionsEncountered.length > 0 && (
                <>
                  <h3>💬 Objections Encountered</h3>
                  <ul>
                    {evaluation.objectionsEncountered.map((obj, i) => (
                      <li key={i}>
                        {obj}
                        {evaluation.objectionHandlingNotes[i] && (
                          <span className="note"> — {evaluation.objectionHandlingNotes[i]}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <h3>📋 Actionable Feedback</h3>
              <ul>
                {evaluation.actionableFeedback.length > 0 ? (
                  evaluation.actionableFeedback.map((fb, i) => (
                    <li key={i}>{fb}</li>
                  ))
                ) : (
                  <li className="empty">No specific feedback</li>
                )}
              </ul>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowFeedback(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Roleplay;
